import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, filter, first, map, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { environment } from 'src/environments/environment';
import { ChatInvite, ChatPendingInvite, ChatSupervisionPage, ChatSupervisionParams, ChatColleaguesPage, ChatColleagueScope } from '../chat.model';
import { ChatAssignResult, ChatAutoReply, ChatMessage, ChatPresenceMap, ChatQuickReply, ChatUser, ChatVisitRow, ChatDealStages, ChatNextAction, ChatReactionGroup, ChatScheduledMessage } from '../chat.model';
import { CookieService } from 'ngx-cookie-service';

/** chat_capabilities (docs/chat-socket-contract.md, 6.1). */
export interface ChatCapabilities {
  contractVersion: number;
  role: string | null;
  canWriteNotes: boolean;
  canSuggestProperties: boolean;
  canInvite: boolean;
  canAssign: boolean;
  canSetAway: boolean;
  canUseAutoReplies: boolean;
  /* versao 3 (seccao 7) */
  canProposeVisits: boolean;
  canQualify: boolean;
  canTransfer: boolean;
  canMention: boolean;
  /** Secção 12: "enviar mais tarde" (só staff). */
  canScheduleMessages?: boolean;
  /** false = autenticado mas sem conta de chat nesta entidade (contrato 6.1). */
  chatIdentity: boolean;
  /** A identidade de chat deste socket (UserApp.id): o "eu" de tudo o que o chat compara. */
  userAppId?: string;
  /** Secção 9: janelas (segundos) para editar e para apagar para todos; 0 = não disponível. */
  messageEditWindowSeconds: number;
  messageDeleteWindowSeconds: number;
}

/** Contrato 6.1: quem ainda nao recebeu o evento trata todas as chaves como false. */
export const CHAT_NO_CAPABILITIES: ChatCapabilities = {
  contractVersion: 0,
  role: null,
  canWriteNotes: false,
  canSuggestProperties: false,
  canInvite: false,
  canAssign: false,
  canSetAway: false,
  canUseAutoReplies: false,
  canProposeVisits: false,
  canQualify: false,
  canTransfer: false,
  canMention: false,
  canScheduleMessages: false,
  chatIdentity: false,
  messageEditWindowSeconds: 0,
  messageDeleteWindowSeconds: 0
};

/** Motivo pelo qual o chat nao esta autenticado no socket partilhado (contrato, seccao 1). */
export type ChatConnectionIssue =
  | 'no_chat_identity'
  | 'invalid_credentials'
  | 'token_expired'
  | 'session_not_found'
  | 'temporarily_unavailable';

/** Tempo que um handshake pode ficar sem resposta antes de deixar de contar como "em curso". */
const HANDSHAKE_TIMEOUT_MS = 15000;
/** `temporarily_unavailable`: 5s, 10s, 20s ... com teto de 5 minutos. */
const BACKOFF_BASE_MS = 5000;
const BACKOFF_MAX_MS = 5 * 60 * 1000;

/**
 * Servico de chat do CRM.
 *
 * SEM SOCKET PROPRIO (docs/chat-socket-contract.md, seccao 1). O websocket do
 * chat saiu do chat-v2 e vive no real-time-notifications, na MESMA ligacao que o
 * CRM ja abre para as notificacoes. Esta classe estendia `Socket` e ligava a
 * `environment.chat`; agora usa o socket do NotificationService e nunca cria
 * outro. `environment.chat` fica so para HTTP (anexos e imagens);
 * `environment.chatPath` deixou de ser usado.
 *
 * A autenticacao e o cookie `auth-cookie`, lido pelo servidor no handshake. Daqui
 * nao se ve o cookie, por isso "nao repetir com o mesmo token" e garantido por
 * episodio: uma renovacao no maximo, e depois de uma recusa o chat fica bloqueado
 * ate `disconnect()` (logout) seguido de novo `connect()` (login).
 *
 * "Ligado" nao e "autenticado": um cookie expirado deixa o socket de pe, mas
 * anonimo. O chat so esta PRONTO quando o servidor o diz - `chat_capabilities`,
 * que so e enviado a um socket com identidade de chat.
 *
 * Os nomes `connect()` / `disconnect()` / `connected` mantem-se porque o
 * AuthService ja os usa, mas passaram a falar do CHAT e nao do transporte:
 * `disconnect()` NAO desliga o socket, que e das notificacoes.
 */
@Injectable(
  { providedIn: 'root' }
)
export class ChatService implements OnDestroy {
  public usersOnline$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public capabilities$: BehaviorSubject<ChatCapabilities> =
    new BehaviorSubject<ChatCapabilities>(CHAT_NO_CAPABILITIES);
  /** Ultima recusa do handshake; null quando o chat esta pronto. */
  public connectionIssue$: BehaviorSubject<ChatConnectionIssue | null> =
    new BehaviorSubject<ChatConnectionIssue | null>(null);
  /**
   * true enquanto o chat NAO esta ligado E autenticado (= `!connected`, mas
   * observavel). E o que a pagina usa para o banner "Sem ligacao".
   */
  public chatDisconnected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  /** `chat_presence` (contrato 6.3): userAppId -> estado. Vazio ate o servidor emitir. */
  public presence$: BehaviorSubject<ChatPresenceMap> = new BehaviorSubject<ChatPresenceMap>({});
  /**
   * O estado que o PROPRIO escolheu com `set_presence` ('online' / 'away').
   * Vive aqui e nao na pagina para sobreviver a navegacao: dura enquanto a
   * sessao durar (o servidor mantem o `away` enquanto houver socket) e cai em
   * `disconnect()` (logout).
   */
  public myPresence$: BehaviorSubject<'online' | 'away'> = new BehaviorSubject<'online' | 'away'>('online');
  /** `GET /chat/quick-replies`, carregado uma vez por sessao; invalidado pelas definicoes. */
  private quickRepliesCache: ChatQuickReply[] | null = null;

  private readonly lifecycle = new Subscription();
  /** O AuthService pediu o chat (`connect()`) e ainda nao o largou (`disconnect()`). */
  private wanted = false;
  /** O servidor confirmou a identidade de chat NESTA ligacao. */
  private ready = false;
  /** Handshake em curso. Limitado por handshakeTimer. */
  private awaiting = false;
  private handshakeTimer: any = null;
  /** Credenciais recusadas e nao renovaveis: nada de novo para apresentar. */
  private blocked = false;
  /** Uma renovacao por episodio; repoe-se quando o chat fica pronto. */
  private refreshTried = false;
  private backoffAttempt = 0;
  private backoffTimer: any = null;
  /** Muda em cada `disconnect()`: uma renovacao pedida antes do logout nao decide nada depois dele. */
  private episode = 0;

  constructor(private http: HttpClient, private socket: NotificationService, private cookies: CookieService) {
    this.lifecycle.add(
      this.socket.on<any>('disconnect').subscribe((args: any) => {
        // O socket.io emite (reason, description); o fromEvent transforma 2+
        // argumentos num array.
        this.onDisconnect(Array.isArray(args) ? args[0] : args);
      })
    );
    this.lifecycle.add(
      this.socket.on<any>('exception').subscribe((body: any) => this.onException(body))
    );
    this.lifecycle.add(
      this.socket.on<any>('chat_capabilities').subscribe((caps: any) => {
        if (!caps || typeof caps !== 'object') {
          return;
        }
        this.capabilities$.next({ ...CHAT_NO_CAPABILITIES, ...caps });
        // Chega a todo o socket autenticado: o handshake ficou resolvido. Com
        // `chatIdentity: false` este utilizador nao tem conta de chat na
        // entidade - religar nao muda nada, por isso fica como motivo e nao
        // como nova tentativa (contrato 6.1).
        this.markReady();
        if (caps.chatIdentity === false) {
          this.connectionIssue$.next('no_chat_identity');
        }
      })
    );
    this.lifecycle.add(
      // Recusa de um handler por falta de conta de chat (contrato, seccao 4):
      // nao e problema de ligacao, e a interface deve dize-lo e nao insistir.
      this.socket.on<any>('error').subscribe((body: any) => {
        if (body && body.auth === true && body.code === 'no_chat_identity') {
          this.connectionIssue$.next('no_chat_identity');
        }
      })
    );
    this.lifecycle.add(
      // O staff recebe a lista da entidade (contrato, seccao 3).
      this.socket.on<any[]>('chat_users_online').subscribe((data: any[]) => {
        this.usersOnline$.next(Array.isArray(data) ? data : []);
      })
    );
    this.lifecycle.add(
      // Presenca fina (online / away / offline) por utilizador (contrato 6.3).
      this.socket.on<any>('chat_presence').subscribe((p: any) => {
        if (!p || typeof p !== 'object' || !p.userAppId) {
          return;
        }
        const status = p.status === 'online' || p.status === 'away' || p.status === 'offline' ? p.status : null;
        if (!status) {
          return;
        }
        this.presence$.next({ ...this.presence$.value, [String(p.userAppId)]: status });
      })
    );
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.lifecycle.unsubscribe();
  }

  /**
   * Ligacao
   */

  /** true quando o chat esta ligado E autenticado (nao e o estado do transporte). */
  get connected(): boolean {
    return this.ready && this.socket.connected;
  }

  /**
   * O utilizador tem acesso ao chat. Liga o socket partilhado mesmo onde o
   * NotificationService nao o faz sozinho (`connectedNotification()` so liga em
   * producao): sem socket nao ha chat.
   */
  connect(): this {
    this.wanted = true;
    this.ensureConnection();
    return this;
  }

  /**
   * Logout, troca de conta ou utilizador sem acesso. NAO desliga o socket - e o
   * das notificacoes, e quem o desliga e `disconnectNotification()`. So larga o
   * estado do chat e levanta o bloqueio, para o proximo login poder ligar.
   */
  disconnect(): this {
    this.episode++;
    this.wanted = false;
    this.awaiting = false;
    this.blocked = false;
    this.refreshTried = false;
    this.backoffAttempt = 0;
    this.clearTimers();
    this.usersOnline$.next([]);
    this.presence$.next({});
    this.myPresence$.next('online');
    this.quickRepliesCache = null;
    this.connectionIssue$.next(null);
    // Fim de sessao: a proxima conta comeca sem capacidades herdadas.
    if (this.capabilities$.value !== CHAT_NO_CAPABILITIES) {
      this.capabilities$.next(CHAT_NO_CAPABILITIES);
    }
    this.setDisconnected(true);
    return this;
  }

  /** Reflete `ready` em `chatDisconnected$` sem repetir emissoes iguais. */
  private setDisconnected(value: boolean): void {
    if (this.chatDisconnected$.value !== value) {
      this.chatDisconnected$.next(value);
    }
  }

  /** Mantido por compatibilidade com o AuthService. A presenca e ligada uma vez, no construtor. */
  chatUsersOnline() {
    /* no-op */
  }

  /**
   * Todos os caminhos que se podiam repetir para sempre acabam aqui, num `return`:
   * ja pronto, handshake em curso, ou credenciais ja recusadas.
   */
  private ensureConnection(): void {
    if (!this.wanted || this.blocked || this.awaiting) {
      return;
    }
    if (this.socket.connected) {
      // De pe. Se esta autenticado, o `chat_capabilities` ja chegou ou vem a
      // caminho; se esta anonimo, quem trata disso e o onException().
      return;
    }
    this.startHandshake();
    this.socket.connect();
  }

  /**
   * Nova ligacao, para o servidor voltar a ler o cookie (a identidade e fixada no
   * handshake). Nao depende de `wanted`: o socket e partilhado, e um cookie
   * expirado deixa tambem as NOTIFICACOES mudas (socket anonimo, sem salas) para
   * quem nao tem chat. A recuperacao e uma so, para os dois.
   */
  private reconnect(): void {
    // Depois de um logout (socket desligado, chat largado) nao ha nada a religar,
    // mesmo que uma renovacao ou um recuo ainda estivessem pendentes.
    if (this.blocked || (!this.wanted && !this.socket.connected)) {
      return;
    }
    this.startHandshake();
    if (this.socket.connected) {
      this.socket.disconnect();
    }
    this.socket.connect();
  }

  private startHandshake(): void {
    this.awaiting = true;
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
    }
    this.handshakeTimer = setTimeout(() => {
      this.handshakeTimer = null;
      this.awaiting = false;
    }, HANDSHAKE_TIMEOUT_MS);
  }

  /**
   * Pede ao servidor para voltar a resolver a identidade de chat deste socket
   * (contrato 6.1). Usa-se quando `chat_capabilities` veio com chatIdentity=false
   * e o REST do CRM ja respondeu - o back-end acabou de provisionar a conta.
   */
  refreshChatIdentity(): void {
    if (!this.wanted) return;
    this.socket.emit('refresh_chat_identity', {});
  }

  private markReady(): void {
    this.ready = true;
    this.blocked = false;
    this.refreshTried = false;
    this.backoffAttempt = 0;
    this.settleHandshake();
    if (this.backoffTimer) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
    if (this.connectionIssue$.value !== null) {
      this.connectionIssue$.next(null);
    }
    this.setDisconnected(false);
  }

  private onDisconnect(reason?: string): void {
    this.ready = false;
    this.setDisconnected(true);
    // As capacidades (e o `userAppId`, o "eu" do chat) ficam ate ao proximo
    // `chat_capabilities`: um soluco de rede nao pode trocar as bolhas de lado
    // nem tirar o modo nota. So o logout (`disconnect()`) as apaga.
    if (reason === 'io server disconnect' && !this.blocked) {
      // O servidor so expulsa por `invalid_credentials`. Se o `exception` que o
      // explica nao chegou, o desfecho e o mesmo: nao insistir com este cookie.
      this.refuse('invalid_credentials');
    }
  }

  /**
   * Respostas ao handshake (contrato, seccao 1). So se tratam corpos com `code`
   * conhecido: o lado das notificacoes usa `exception` para outras coisas, e um
   * codigo novo de um servidor mais recente tem de ser ignorado.
   */
  private onException(body: any): void {
    const code: string = body && body.code;
    if (!code) {
      return;
    }
    switch (code) {
      case 'invalid_credentials':
        // O servidor desliga a seguir. Nao se volta a tentar com este cookie: e
        // preciso novo login.
        this.refuse('invalid_credentials');
        break;

      case 'token_expired':
      case 'session_not_found':
        // O socket fica de pe, anonimo, e `reconnect: false` diz que o socket.io
        // nao deve insistir. Renova-se pelo mecanismo do CRM (GET /refresh, que
        // reescreve o cookie) e liga-se de novo - uma vez.
        this.settleHandshake();
        this.connectionIssue$.next(code);
        this.renewAndReconnect(code);
        break;

      case 'temporarily_unavailable':
        this.settleHandshake();
        this.connectionIssue$.next(code);
        this.scheduleBackoff();
        break;

      default:
        break;
    }
  }

  private renewAndReconnect(code: ChatConnectionIssue): void {
    if (this.blocked) {
      return;
    }
    if (this.refreshTried) {
      this.refuse(code);
      return;
    }
    this.refreshTried = true;
    const episode = this.episode;
    // O mesmo pedido de AuthService.refreshCookie(). Nao se injeta o AuthService
    // porque ele ja injeta este servico (dependencia circular). O jwt.interceptor
    // deixa passar '/refresh' sem tratamento de 401, por isso uma falha chega aqui
    // inteira (HttpErrorResponse, com `status`).
    this.http.get<any>(`${environment.apiUrl}/refresh`, { withCredentials: true })
      .pipe(first())
      .subscribe({
        next: () => {
          if (episode === this.episode) {
            this.reconnect();
          }
        },
        error: (err: any) => {
          if (episode !== this.episode) {
            // Houve logout entretanto: recusar agora deixava o PROXIMO login
            // bloqueado (`blocked` so e levantado em disconnect()).
            return;
          }
          // Sem rede ou 5xx nao diz nada sobre as credenciais. Tratar isso como
          // recusa deixava chat E notificacoes mudos ate ao proximo login por
          // causa de um soluco (portatil a acordar, API a reiniciar). Tenta-se
          // mais tarde, com o mesmo intervalo crescente de `temporarily_unavailable`.
          const status: number = err && typeof err.status === 'number' ? err.status : -1;
          if (status === 0 || status >= 500) {
            this.refreshTried = false;
            this.scheduleBackoff();
            return;
          }
          this.refuse(code);
        }
      });
  }

  /** Recuo exponencial com teto e jitter; repoe-se quando o chat fica pronto. */
  private scheduleBackoff(): void {
    if (this.backoffTimer || this.blocked) {
      return;
    }
    const exp = Math.min(BACKOFF_BASE_MS * Math.pow(2, this.backoffAttempt), BACKOFF_MAX_MS);
    const delay = Math.round(exp * (0.75 + Math.random() * 0.5));
    this.backoffAttempt++;
    this.backoffTimer = setTimeout(() => {
      this.backoffTimer = null;
      this.reconnect();
    }, delay);
  }

  private refuse(code: ChatConnectionIssue): void {
    this.settleHandshake();
    this.blocked = true;
    this.ready = false;
    this.setDisconnected(true);
    this.connectionIssue$.next(code);
  }

  private settleHandshake(): void {
    this.awaiting = false;
    if (this.handshakeTimer) {
      clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }

  private clearTimers(): void {
    this.settleHandshake();
    if (this.backoffTimer) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
  }

  /**
   * Eventos de chat do socket partilhado. Sem acesso ao chat (`wanted` a false)
   * nao passam: antes o socket do chat estava desligado nesse caso, e o efeito
   * tem de ser o mesmo. Receber um deles tambem prova que o chat esta pronto.
   */
  private chatEvent<T>(eventName: string): Observable<T> {
    return this.socket.on<T>(eventName).pipe(
      filter(() => this.wanted),
      tap(() => {
        if (!this.ready) {
          this.markReady();
        }
      })
    );
  }

  /***
  * Get all messages
  */
  getAllMessages(uc: any) {

    return this.http.get<ChatMessage[]>(`${environment.apiUrl}/chat/messages/${uc}`, { withCredentials: true })
      .pipe(first(), map(data => data),
        catchError(error => { return of<ChatMessage[]>([]) }));
  }

  /***
* Get all messages
*/
  getAllUsersChat() {

    return this.http.get<ChatUser[]>(`${environment.apiUrl}/chat/users`, { withCredentials: true })
      .pipe(first(), map(data => data),
        catchError(error => { return of<ChatUser[]>([]) }));
  }

  /** `send_message` (contrato 2 / 6.4): `clientMessageId` e devolvido no eco e evita duplicados ao reenviar. */
  sendMessage(data: { IdReceiver?: string; chat?: string; message?: string; propertyId?: any; clientMessageId?: string; replyTo?: string; }) {
    this.socket.emit('send_message', this.compact(data));
  }

  /** `send_note` (contrato 6.3): nota interna numa conversa existente. Exige canWriteNotes; o servidor volta a verificar. */
  sendNote(data: { chat: string; message: string; clientMessageId?: string; mentions?: string[]; replyTo?: string }) {
    this.socket.emit('send_note', this.compact({
      chat: data.chat, message: data.message, clientMessageId: data.clientMessageId,
      mentions: data.mentions && data.mentions.length > 0 ? data.mentions : undefined,
      // Secção 10: a mensagem citada (o servidor valida: mesma conversa, nunca sistema).
      replyTo: data.replyTo
    }));
  }

  /** `send_property` (contrato 6.3): um cartao de imovel. `propertyId` e o uuid do CRM. Exige canSuggestProperties. */
  sendProperty(data: { chat?: string; IdReceiver?: string; propertyId: string; message?: string; clientMessageId?: string }) {
    this.socket.emit('send_property', this.compact(data));
  }

  /** `set_presence` (contrato 6.3). Otimista: o servidor confirma por `chat_presence`. Exige canSetAway. */
  setPresence(status: 'online' | 'away') {
    this.myPresence$.next(status);
    this.socket.emit('set_presence', { status });
  }

  /**
   * `invite_to_chat` (contrato 6.3, v8): um CONVITE. O convidado recebe `chat_invite` e
   * so entra ao aceitar; o staff da conversa recebe `chat_invite_sent`.
   */
  inviteToChat(data: { chat: string; userAppId: string }) {
    this.socket.emit('invite_to_chat', { chat: data.chat, userAppId: data.userAppId });
  }

  /** `answer_invite` (v8): aceitar ou recusar um convite meu. null = recusado pelo servidor ou sem resposta. */
  answerInvite(chat: string, accept: boolean): Observable<{ ok: boolean; accepted?: boolean } | null> {
    return this.emitWithAck('answer_invite', { chat, accept });
  }

  /** `cancel_invite` (v8): retirar um convite por aceitar desta conversa. */
  cancelInvite(chat: string, userAppId: string): Observable<{ ok: boolean } | null> {
    return this.emitWithAck('cancel_invite', { chat, userAppId });
  }

  /** `chat_invite` (v8): fui convidado para uma conversa. */
  chatInvite() {
    return this.chatEvent<ChatInvite>('chat_invite');
  }

  /** `chat_invite_sent` (v8): alguem desta conversa foi convidado (a quem convidou e ao staff). */
  chatInviteSent() {
    return this.chatEvent<{ chat: string; userAppId: string; name?: string; profile?: string; invitedAt: string; by: string }>('chat_invite_sent');
  }

  /** `chat_invite_answered` (v8): um convite foi recusado (ao staff da conversa e ao proprio). */
  chatInviteAnswered() {
    return this.chatEvent<{ chat: string; userAppId: string; name?: string; accepted: boolean }>('chat_invite_answered');
  }

  /** `chat_invite_cancelled` (v8): um convite foi retirado (ao convidado e ao staff). */
  chatInviteCancelled() {
    return this.chatEvent<{ chat: string; userAppId: string }>('chat_invite_cancelled');
  }

  /**
   * Um emit com ack, como Observable de UM valor. Uma recusa do servidor vem pelo
   * evento `error` (marca inviteChat...) e o ack nunca chega: ao fim de `timeoutMs`
   * sai null.
   */
  private emitWithAck<T>(event: string, payload: any, timeoutMs = 8000): Observable<T | null> {
    return new Observable<T | null>(subscriber => {
      let done = false;
      const timer = setTimeout(() => {
        if (!done) {
          done = true;
          subscriber.next(null);
          subscriber.complete();
        }
      }, timeoutMs);
      this.socket.emit(event, payload, (ack: T) => {
        if (done) {
          return;
        }
        done = true;
        clearTimeout(timer);
        subscriber.next(ack ?? null);
        subscriber.complete();
      });
      return () => clearTimeout(timer);
    });
  }

  /** `typing` (contrato 6.3). Quem chama e que limita a cadencia. */
  /** `typing`: a escrever (`text`) ou a gravar uma mensagem de voz (`voice`). */
  sendTyping(data: { chat: string; typing: boolean; mode?: 'text' | 'voice' }) {
    this.socket.emit('typing', { chat: data.chat, typing: !!data.typing, ...(data.mode === 'voice' ? { mode: 'voice' } : {}) });
  }

  /** `chat_assigned { chat, userAppId, by }` (contrato 6.3): participantes e supervisores. */
  chatAssigned() {
    return this.chatEvent<{ chat: string; userAppId: string; by: string }>('chat_assigned');
  }

  /* --- versao 3 (seccao 7): visitas e anuncios de mensagens criadas por HTTP --- */

  /** `send_visit_request` (7.1): ate 5 horarios ISO; so staff (canProposeVisits). */
  sendVisitRequest(data: { chat: string; slots: string[]; durationMinutes?: number; note?: string; propertyId?: string | number }) {
    this.socket.emit('send_visit_request', this.compact({
      chat: data.chat, slots: data.slots, durationMinutes: data.durationMinutes, note: data.note,
      // 2026-10-27: o imóvel do modal (o agendamento automático usa-o; sem ele, o da conversa).
      propertyId: data.propertyId !== undefined && data.propertyId !== null ? String(data.propertyId) : undefined
    }));
  }

  /** `answer_visit_request` (7.1): escolher um slot, propor outro horario ou recusar. */
  answerVisitRequest(data: { chat: string; messageId: string; chosen?: string; proposed?: string; declined?: boolean }) {
    this.socket.emit('answer_visit_request', this.compact({
      chat: data.chat, messageId: data.messageId, chosen: data.chosen, proposed: data.proposed, declined: data.declined || undefined
    }));
  }

  /** `send_qualification` (7.1): bloco de qualificacao para o cliente responder; so staff (canQualify). */
  sendQualification(data: { chat: string; fields?: string[] }) {
    this.socket.emit('send_qualification', this.compact({ chat: data.chat, fields: data.fields }));
  }

  /** `answer_qualification` (7.1): as respostas (o staff pode editar em nome do cliente). */
  answerQualification(data: { chat: string; messageId: string; answers: any }) {
    this.socket.emit('answer_qualification', { chat: data.chat, messageId: data.messageId, answers: data.answers || {} });
  }

  /** `transfer_chat` (7.1): passa a responsabilidade a outro staff, com nota de contexto. */
  transferChat(data: { chat: string; userAppId: string; note?: string }) {
    this.socket.emit('transfer_chat', this.compact({ chat: data.chat, userAppId: data.userAppId, note: data.note }));
  }

  /** `leave_chat` (7.1): sair de uma conversa em que fui convidado/mencionado (o responsavel transfere). */
  leaveChat(chat: string) {
    this.socket.emit('leave_chat', { chat });
  }

  /** `remove_participant` (7.1): o responsavel (ou quem transfere) retira um colega. */
  removeParticipant(data: { chat: string; userAppId: string }) {
    this.socket.emit('remove_participant', { chat: data.chat, userAppId: data.userAppId });
  }

  /** `chat_participant_removed` (7.1): so ao staff da conversa. */
  chatParticipantRemoved() {
    return this.chatEvent<{ chat: string; userAppId: string; by: string }>('chat_participant_removed');
  }

  /**
   * `announce_message` (7.1): uma linha criada por REST no back-end e entregue a todos os
   * participantes. Com `update: true` a linha ja existia (cartao cancelado / fechado) e
   * os participantes recebem so o estado novo (`receive_message_update`), nao uma mensagem.
   */
  announceMessage(data: { messageId: string; idTemp?: number | string; update?: boolean }) {
    this.socket.emit('announce_message', this.compact({ messageId: data.messageId, idTemp: data.idTemp, update: data.update || undefined }));
  }

  /** `receive_message_update` (7.1): o estado de um cartao mudou (slot escolhido, ficha respondida...). */
  receiveMessageUpdate() {
    return this.chatEvent<{ chat: string; messageId: string; kind: string; params: any }>('receive_message_update');
  }

  /** `chat_participant_added { chat, participant }` (contrato 6.3). */
  chatParticipantAdded() {
    return this.chatEvent<{ chat: string; participant: { id: string; name?: string; profile?: string } }>('chat_participant_added');
  }

  /** `chat_typing { chat, userAppId, typing }` (contrato 6.3): so os outros participantes. */
  chatTyping() {
    return this.chatEvent<{ chat: string; userAppId: string; typing: boolean; mode?: 'text' | 'voice' }>('chat_typing');
  }

  /** Um payload de socket sem chaves vazias (o servidor valida o corpo). */
  private compact<T extends object>(data: T): T {
    const out: any = {};
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        out[key] = value;
      }
    });
    return out as T;
  }

  /* -------------------------------------------------------------------
   * REST v2 do back-end (docs/chat-socket-contract.md, seccao 6; rotas
   * `/chat/queue`, `/chat/:chat/assign`, `/chat/auto-replies`, `/chat/quick-replies`).
   * Ao contrario dos GET antigos, os erros de escrita NAO sao engolidos: a
   * pagina mostra a mensagem do servidor.
   * ---------------------------------------------------------------- */

  /** Colegas para nova conversa / convidar / transferir (2026-10-28): ativos, email confirmado, loja ou rede. */
  getColleagues(params: { scope?: ChatColleagueScope; storeId?: number | null; search?: string }): Observable<ChatColleaguesPage> {
    const query: { [key: string]: string } = {};
    if (params.scope) { query['scope'] = params.scope; }
    if (params.storeId) { query['storeId'] = String(params.storeId); }
    if (params.search) { query['search'] = params.search; }
    return this.http.get<ChatColleaguesPage>(`${environment.apiUrl}/chat/colleagues`, { params: query, withCredentials: true })
      .pipe(first(), map(page => ({ scope: page && page.scope === 'network' ? 'network' : 'store', master: !!(page && page.master), stores: (page && page.stores) || [], users: (page && page.users) || [] })));
  }

  /** Conversas por atribuir no ambito de quem pede, mais antigas primeiro, com `waitingSince`. */
  getQueue(): Observable<ChatUser[]> {
    return this.http.get<ChatUser[]>(`${environment.apiUrl}/chat/queue`, { withCredentials: true })
      .pipe(first(), map(data => Array.isArray(data) ? data : []), catchError(() => of<ChatUser[]>([])));
  }

  /**
   * Supervisão (2026-10-27): os consultores da equipa, `limit` de cada vez, com o
   * total de conversas, os clientes à espera e a última atividade. Os erros sobem
   * (a vista mostra "Tentar de novo").
   */
  getSupervisionGroups(params: ChatSupervisionParams): Observable<ChatSupervisionPage> {
    return this.http.get<ChatSupervisionPage>(`${environment.apiUrl}/chat/supervision`, {
      withCredentials: true, params: this.supervisionQuery(params)
    }).pipe(first());
  }

  /** As conversas de um consultor na Supervisão (`none` = "Sem responsável"). */
  getSupervisionChats(group: string, params: ChatSupervisionParams): Observable<ChatUser[]> {
    return this.http.get<ChatUser[]>(`${environment.apiUrl}/chat/supervision/${encodeURIComponent(group)}/chats`, {
      withCredentials: true, params: this.supervisionQuery(params)
    }).pipe(first(), map(data => Array.isArray(data) ? data : []));
  }

  private supervisionQuery(params: ChatSupervisionParams): { [key: string]: string } {
    const query: { [key: string]: string } = {
      kind: params.kind, filter: params.filter, offset: String(params.offset), limit: String(params.limit)
    };
    if (params.search) {
      query['search'] = params.search;
    }
    return query;
  }

  /** Contrato 6.3, v8: os meus convites por aceitar (mais recentes primeiro). */
  getInvites(): Observable<ChatInvite[]> {
    return this.http.get<ChatInvite[]>(`${environment.apiUrl}/chat/invites`, { withCredentials: true })
      .pipe(first(), map(data => Array.isArray(data) ? data : []), catchError(() => of<ChatInvite[]>([])));
  }

  /** Contrato 6.3, v8: quem foi convidado para esta conversa e ainda nao respondeu. */
  getPendingInvites(chat: string): Observable<ChatPendingInvite[]> {
    return this.http.get<ChatPendingInvite[]>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/pending-invites`, { withCredentials: true })
      .pipe(first(), map(data => Array.isArray(data) ? data : []), catchError(() => of<ChatPendingInvite[]>([])));
  }

  /** 201 com o responsavel e a mensagem de sistema; 403 sem permissao; 404 alvo fora do ambito. */
  assignChat(chat: string, userAppId: string): Observable<ChatAssignResult> {
    return this.http.post<ChatAssignResult>(
      `${environment.apiUrl}/chat/${encodeURIComponent(chat)}/assign`, { userAppId }, { withCredentials: true }
    ).pipe(first());
  }

  /* --- 7.3: acoes da conversa no back-end --- */

  ensureDeal(chat: string): Observable<{ leadId: number; dealId: number }> {
    return this.http.post<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/ensure-deal`, {}, { withCredentials: true }).pipe(first());
  }

  getChatVisits(chat: string): Observable<ChatVisitRow[]> {
    return this.http.get<ChatVisitRow[]>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/visits`, { withCredentials: true })
      .pipe(first(), map(data => Array.isArray(data) ? data : []));
  }

  createChatVisit(chat: string, body: {
    startAt: string; durationMinutes?: number; propertyId?: string; requestMessageId?: string;
    sendClientEmail?: boolean; emailSubject?: string; emailBody?: string;
  }): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/visits`, this.compact(body), { withCredentials: true })
      .pipe(first());
  }

  /** Secção 11: horários livres do consultor da conversa (9h–19h, seg–sáb), para os atalhos dos modais. */
  getVisitSlots(chat: string, params: { from?: string; days?: number; durationMinutes?: number } = {}): Observable<{ consultantUserId?: string; durationMinutes: number; slots: string[] }> {
    const query: { [key: string]: string } = {};
    if (params.from) { query['from'] = params.from; }
    if (params.days) { query['days'] = String(params.days); }
    if (params.durationMinutes) { query['durationMinutes'] = String(params.durationMinutes); }
    return this.http.get<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/visit-slots`, { params: query, withCredentials: true })
      .pipe(first(), map(res => ({ ...res, slots: res && Array.isArray(res.slots) ? res.slots : [] })), catchError(() => of({ durationMinutes: params.durationMinutes || 60, slots: [] })));
  }

  /** Secção 11: novo horário para uma visita ainda marcada; os cartões `visit` vêm em `messageIds`. */
  rescheduleChatVisit(chat: string, visitId: number, body: { startAt: string; durationMinutes?: number }): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/chat/${encodeURIComponent(chat)}/visits/${encodeURIComponent(String(visitId))}/reschedule`, this.compact(body), { withCredentials: true }
    ).pipe(first());
  }

  cancelChatVisit(chat: string, visitId: number, reason: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/chat/${encodeURIComponent(chat)}/visits/${encodeURIComponent(String(visitId))}/cancel`, { reason }, { withCredentials: true }
    ).pipe(first());
  }

  /** Ficha de visita virtual: emite o link (e, com `chatId`, o cartao na conversa). */
  issueVirtualVisitForm(visitId: number, chatId?: string): Observable<{ token: string; url: string; formId: number; messageId?: string | null }> {
    return this.http.post<any>(
      `${environment.apiUrl}/visit-form/visits/${encodeURIComponent(String(visitId))}/virtual`, this.compact({ chatId }), { withCredentials: true }
    ).pipe(first());
  }

  /** Proposta na conversa (7.3): sem id, a mais recente da lead (o modal de propostas nao devolve o id). */
  shareProposal(chat: string, proposalId?: number): Observable<{ messageId: string; proposalId: number }> {
    return this.http.post<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/messages/proposal`, this.compact({ proposalId }), { withCredentials: true })
      .pipe(first());
  }

  createReminder(chat: string, at: string, note: string): Observable<{ eventId: number; at: string; note: string }> {
    return this.http.post<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/reminders`, { at, note }, { withCredentials: true }).pipe(first());
  }

  getNextAction(chat: string): Observable<ChatNextAction | null> {
    return this.http.get<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/next-action`, { withCredentials: true })
      .pipe(first(), map(data => (data && data.type ? data : null)), catchError(() => of(null)));
  }

  getDealStages(chat: string): Observable<ChatDealStages | null> {
    return this.http.get<any>(`${environment.apiUrl}/chat/${encodeURIComponent(chat)}/deal`, { withCredentials: true })
      .pipe(first(), catchError(() => of(null)));
  }

  /** `PATCH deals/change-status/:id/:lang?type=sale` (o mesmo do funil; exige MOVE_DEALS no servidor). */
  changeDealStatus(dealId: number, statusId: number): Observable<any> {
    const lang = this.cookies.get('lang') || 'pt';
    return this.http.patch<any>(`${environment.apiUrl}/deals/change-status/${dealId}/${lang}?type=sale`, { idStatus: statusId }, { withCredentials: true })
      .pipe(first());
  }

  /** Aviso de sobreposicao na agenda de quem pede (nao bloqueia). */
  checkVisitAvailability(startAt: string, durationMinutes: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/deals/visits/availability`, {
      params: { startAt, durationMinutes: String(durationMinutes) }, withCredentials: true
    }).pipe(first(), catchError(() => of(null)));
  }

  getAutoReplies(): Observable<ChatAutoReply[]> {
    return this.http.get<ChatAutoReply[]>(`${environment.apiUrl}/chat/auto-replies`, { withCredentials: true })
      .pipe(first(), map(data => Array.isArray(data) ? data : []));
  }

  /** `userAppId` omitido = propria; `null` = geral da agencia (so canAssign). */
  createAutoReply(body: { trigger: string; message: string; status?: boolean; userAppId?: string | null }): Observable<ChatAutoReply> {
    return this.http.post<ChatAutoReply>(`${environment.apiUrl}/chat/auto-replies`, body, { withCredentials: true }).pipe(first());
  }

  updateAutoReply(id: string, body: { message?: string; status?: boolean }): Observable<ChatAutoReply> {
    return this.http.put<ChatAutoReply>(`${environment.apiUrl}/chat/auto-replies/${encodeURIComponent(id)}`, body, { withCredentials: true })
      .pipe(first());
  }

  deleteAutoReply(id: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}/chat/auto-replies/${encodeURIComponent(id)}`, { withCredentials: true }).pipe(first());
  }

  /**
   * Proprias + gerais. Carregado UMA vez por sessao (o compositor precisa
   * delas em cada conversa); `force` depois de criar/editar/apagar.
   */
  getQuickReplies(force = false): Observable<ChatQuickReply[]> {
    if (this.quickRepliesCache && !force) {
      return of(this.quickRepliesCache);
    }
    return this.http.get<ChatQuickReply[]>(`${environment.apiUrl}/chat/quick-replies`, { withCredentials: true })
      .pipe(
        first(),
        map(data => Array.isArray(data) ? data : []),
        tap(list => this.quickRepliesCache = list)
      );
  }

  createQuickReply(body: { shortcut: string; message: string; status?: boolean; userAppId?: string | null }): Observable<ChatQuickReply> {
    this.quickRepliesCache = null;
    return this.http.post<ChatQuickReply>(`${environment.apiUrl}/chat/quick-replies`, body, { withCredentials: true }).pipe(first());
  }

  updateQuickReply(id: string, body: { shortcut?: string; message?: string; status?: boolean }): Observable<ChatQuickReply> {
    this.quickRepliesCache = null;
    return this.http.put<ChatQuickReply>(`${environment.apiUrl}/chat/quick-replies/${encodeURIComponent(id)}`, body, { withCredentials: true })
      .pipe(first());
  }

  deleteQuickReply(id: string): Observable<any> {
    this.quickRepliesCache = null;
    return this.http.delete<any>(`${environment.apiUrl}/chat/quick-replies/${encodeURIComponent(id)}`, { withCredentials: true }).pipe(first());
  }

  /**
   * Os anexos vao por HTTP, nao pelo socket (contrato, seccao 5).
   *
   * Os `File` seguem em multipart, tal como vieram do input: sem base64. A
   * resposta JA E a mensagem, no formato de `receive_message_file`; quem chama
   * usa-a para substituir o placeholder de imediato. O anuncio faz o servidor
   * entregar `receive_message_file` ao outro participante - e a nos outra vez, o
   * que quem chama tem de absorver de forma idempotente, por `messageId`.
   */
  sendFiles(data: { IdReceiver: string; files: File[]; chat?: string; propertyId?: any; idTemp?: number | string; clientMessageId?: string; voiceDuration?: number }): Observable<any> {
    const form = new FormData();
    // Mensagem de voz: a duração em segundos; o servidor só aceita áudio com ela.
    if (data.voiceDuration) {
      form.append('voiceDuration', String(Math.round(data.voiceDuration)));
    }
    // Campos de texto primeiro: um parser de multipart em streaming so conhece,
    // enquanto trata um ficheiro, os campos que vieram antes dele.
    form.append('IdReceiver', String(data.IdReceiver));
    if (data.chat) {
      form.append('chat', String(data.chat));
    }
    if (data.propertyId !== undefined && data.propertyId !== null && data.propertyId !== '') {
      form.append('propertyId', String(data.propertyId));
    }
    if (data.idTemp !== undefined && data.idTemp !== null) {
      form.append('idTemp', String(data.idTemp));
    }
    if (data.clientMessageId) {
      form.append('clientMessageId', data.clientMessageId);
    }
    (data.files || []).forEach((file: File) => form.append('files', file, file.name));

    return this.http.post<any>(`${environment.chat}/chatv2/chat/messages/files`, form, { withCredentials: true })
      .pipe(
        first(),
        tap((message: any) => {
          if (message && message.messageId) {
            this.socket.emit('announce_files', { messageId: message.messageId });
          }
        })
      );
  }

  /** `delete_messages` (secção 9): apagar para todos. Só o autor, até 60 h; a linha fica como "apagada". */
  deleteMessage(data: any) {
    this.socket.emit('delete_messages', data);
  }

  /** `edit_message` (secção 9): só o autor, texto ou nota, até 15 min depois de enviar. */
  editMessage(data: { chat: string; messageId: string; message: string }) {
    this.socket.emit('edit_message', { chat: data.chat, messageId: data.messageId, message: data.message });
  }

  /** `receive_message_edit` (secção 9): o texto novo de uma mensagem, a todos (numa nota, só ao staff). */
  receiveMessageEdit() {
    return this.chatEvent<{ chat: string; messageId: string; message: string; editedAt: any; time?: any }>('receive_message_edit');
  }

  receiveMessageDelete() {
    return this.chatEvent<any>('receive_message_delete');
  }

  /* --- secção 12: reações, apagar para mim, enviar mais tarde --- */

  /** `react_message`: um emoji por utilizador e mensagem; `null` tira. O servidor ecoa `receive_message_reaction`. */
  reactMessage(data: { chat: string; messageId: string; emoji: string | null }) {
    this.socket.emit('react_message', data);
  }

  receiveMessageReaction() {
    return this.chatEvent<{ chat: string; messageId: string; userAppId: string; name?: string; emoji: string | null; reactions: ChatReactionGroup[] }>('receive_message_reaction');
  }

  /** `hide_message`: a mensagem deixa de aparecer só a quem a esconde. */
  hideMessage(data: { chat: string; messageId: string }) {
    this.socket.emit('hide_message', data);
  }

  receiveMessageHidden() {
    return this.chatEvent<{ chat: string; messageId: string }>('receive_message_hidden');
  }

  /** `schedule_message`: fica no servidor e sai em `sendAt`; a lista chega por `scheduled_messages`. */
  scheduleMessage(data: { chat: string; message: string; sendAt: string; kind?: 'text' | 'note' }) {
    this.socket.emit('schedule_message', this.compact(data));
  }

  cancelScheduledMessage(data: { chat: string; id: string }) {
    this.socket.emit('cancel_scheduled_message', data);
  }

  listScheduledMessages(chat: string) {
    this.socket.emit('list_scheduled_messages', { chat });
  }

  scheduledMessages() {
    return this.chatEvent<{ chat: string; items: ChatScheduledMessage[] }>('scheduled_messages');
  }

  scheduledMessageFailed() {
    return this.chatEvent<{ chat: string; id: string; reason: string }>('scheduled_message_failed');
  }

  deleteFiles(data: { messageId: string; fileId: string; chat?: string; }): Observable<any> {
    const url = `${environment.chat}/chatv2/chat/messages/${encodeURIComponent(data.messageId)}`
      + `/files/${encodeURIComponent(data.fileId)}`;
    return this.http.delete<any>(url, { withCredentials: true })
      .pipe(
        first(),
        tap((result: any) => {
          this.socket.emit('announce_file_delete', {
            messageId: data.messageId,
            fileId: data.fileId,
            chat: (result && result.chat) || data.chat
          });
        })
      );
  }

  receiveMessageDeleteFiles() {
    return this.chatEvent<any>('receive_message_delete_files');
  }

  receiveDeleteChat() {
    return this.chatEvent<any>('receive_delete_chat');
  }

  receiveMessage() {
    return this.chatEvent<any>('receive_message');
  }

  receiveMessageFile() {
    return this.chatEvent<any>('receive_message_file');
  }

  readMessage(data: any) {
    this.socket.emit('read_message', data);
  }

  viewedMessage() {
    return this.chatEvent<any>('viewed_message');
  }

  viewedMyMessage() {
    return this.chatEvent<any>('viewed_my_message');
  }

  /**
   * Recusas (contrato, seccao 4). O corpo traz uma marca da operacao
   * (`createMsg`, `readMsg`, `deleteMsg`, `deleteChat`, `auth`); quem ouve decide
   * pela marca e nunca pelo texto de `message`.
   */
  error() {
    return this.socket.on<any>('error');
  }

  chatDisconnect() {
    return this.socket.on<any>('disconnect');
  }

  getImage(url: string): Observable<any> {
    return this.http.get(url, { responseType: 'blob', withCredentials: true });
  }
}
